import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';

export type OrgTrustStatusCode =
  | 'active'
  | 'warned'
  | 'restricted'
  | 'suspended'
  | 'banned';

export type OrgTrustStatus = {
  organizationId: string;
  status: OrgTrustStatusCode;
  statusReason: string | null;
  statusUntil: string | null;
  strikesActive: number;
  statusChangedAt: string | null;
};

const STATUS_TITLE: Record<OrgTrustStatusCode, string> = {
  active: 'Todo en orden',
  warned: 'Atención',
  restricted: 'Tienda online restringida',
  suspended: 'Tienda online suspendida',
  banned: 'Cuenta bloqueada',
};

/** Texto corto para la tarjeta de Inicio (debe caber en ~2 líneas). */
const STATUS_HOME_TEASER: Record<OrgTrustStatusCode, string> = {
  active: 'Tu negocio anda bien.',
  warned: 'Hubo un problema con tu tienda. Podés seguir vendiendo — mirá el detalle.',
  restricted: 'Sin pedidos ni reservas online. El mostrador sigue.',
  suspended: 'Sin tienda online. El mostrador sigue.',
  banned: 'Este negocio no puede operar en Vuelto.',
};

const STATUS_BODY: Record<OrgTrustStatusCode, string> = {
  active: 'Tu negocio anda bien en la tienda online.',
  warned: 'Hubo un problema con tu tienda. Podés seguir vendiendo — mirá el detalle.',
  restricted:
    'Tu tienda online no se ve y no acepta pedidos ni reservas. El mostrador sigue.',
  suspended: 'Tu negocio no puede vender online. El mostrador sigue.',
  banned: 'Este negocio no puede operar en Vuelto. Si creés que es un error, escribinos a soporte.',
};

/** Motivos máquina / legacy → copy para el negocio (nunca mostrar `ops_*`). */
const REASON_LABELS: Record<string, string> = {
  ops_active: 'Reactivado por el equipo de Vuelto',
  ops_warned: 'Advertencia del equipo de Vuelto',
  ops_restricted: 'Restricción aplicada por el equipo de Vuelto',
  ops_suspended: 'Suspensión aplicada por el equipo de Vuelto',
  ops_banned: 'Bloqueo aplicado por el equipo de Vuelto',
  active: 'Reactivado por el equipo de Vuelto',
  warned: 'Advertencia del equipo de Vuelto',
  restricted: 'Restricción aplicada por el equipo de Vuelto',
  suspended: 'Suspensión aplicada por el equipo de Vuelto',
  banned: 'Bloqueo aplicado por el equipo de Vuelto',
  'merchant.store_low_rating': 'Tienda oculta por calificaciones bajas',
  'merchant.chargeback': 'Hubo un contracargo en un pago',
  'merchant.chargeback_threshold': 'Varios contracargos: tienda online suspendida',
  appeal_reduced: 'Apelación aceptada parcialmente',
};

function asStatus(raw: unknown): OrgTrustStatusCode {
  if (
    raw === 'warned' ||
    raw === 'restricted' ||
    raw === 'suspended' ||
    raw === 'banned'
  ) {
    return raw;
  }
  return 'active';
}

/**
 * TTL corto: el hook self-fetchea en mount/AppState y loadHomeData llama
 * refresh() encima — sin dedupe eso dispara la misma RPC dos veces por foco.
 * La pantalla de detalle (MarketplaceTrust) pasa force:true.
 */
const trustCache = createKeyedTtlCache(15_000);

registerSessionCacheClearHandler(() => trustCache.clear());

/** Fetches marketplace trust status for the caller's organization. */
export async function fetchMyOrganizationTrustStatus(opts?: {
  force?: boolean;
}): Promise<OrgTrustStatus | null> {
  return trustCache.get('_', fetchRemoteTrustStatus, { force: opts?.force });
}

/** Parse tolerante — lo reusa el bootstrap (sección `org_trust` embebida). */
export function parseOrgTrustStatus(data: unknown): OrgTrustStatus | null {
  if (!data || typeof data !== 'object') return null;
  const row = data as Record<string, unknown>;
  if (row.ok === false) return null;
  const orgId = typeof row.organization_id === 'string' ? row.organization_id : null;
  if (!orgId) return null;
  return {
    organizationId: orgId,
    status: asStatus(row.status),
    statusReason: typeof row.status_reason === 'string' ? row.status_reason : null,
    statusUntil: typeof row.status_until === 'string' ? row.status_until : null,
    strikesActive: typeof row.strikes_active === 'number' ? row.strikes_active : 0,
    statusChangedAt:
      typeof row.status_changed_at === 'string' ? row.status_changed_at : null,
  };
}

/** El bootstrap embebe `org_trust`; hydrate prima este cache y evita la RPC suelta. */
export function primeTrustStatusCache(status: OrgTrustStatus | null): void {
  trustCache.set('_', status);
}

async function fetchRemoteTrustStatus(): Promise<OrgTrustStatus | null> {
  const { data, error } = await supabase.rpc('get_my_organization_trust_status');
  if (error) {
    console.warn('[orgTrustStatus]', error.message);
    return null;
  }
  return parseOrgTrustStatus(data);
}

export function isTrustAttentionStatus(status: OrgTrustStatusCode): boolean {
  return status === 'warned' || status === 'restricted' || status === 'suspended' || status === 'banned';
}

export function trustStatusTitle(status: OrgTrustStatusCode): string {
  return STATUS_TITLE[status];
}

export function trustStatusBody(status: OrgTrustStatusCode): string {
  return STATUS_BODY[status];
}

export function trustStatusHomeTeaser(status: OrgTrustStatusCode): string {
  return STATUS_HOME_TEASER[status];
}

/** Motivo legible para el negocio; oculta códigos internos. */
export function formatMerchantTrustReason(
  reason: string | null | undefined,
): string | null {
  if (!reason) return null;
  const trimmed = reason.trim();
  if (!trimmed) return null;
  if (REASON_LABELS[trimmed]) return REASON_LABELS[trimmed];
  const opsMatch = /^ops_(.+)$/i.exec(trimmed);
  if (opsMatch) {
    const key = opsMatch[1].toLowerCase();
    return REASON_LABELS[key] ?? 'Medida aplicada por el equipo de Vuelto';
  }
  // Códigos snake_case sin espacios → no mostrar crudo
  if (/^[a-z][a-z0-9_]+$/i.test(trimmed) && trimmed.includes('_')) {
    return 'Medida aplicada por el equipo de Vuelto';
  }
  return trimmed;
}
